import { useState } from 'react';
import type { BoxProps } from '@mantine/core';
import {
  automaticNoteTemplate,
  firmDefaultTemplate,
  needsDefault,
} from 'src/data-capture/hooks/templates/defaultTemplate';
import { useCanChooseDefaultTemplate } from 'src/data-capture/hooks/templates/useCanChooseDefaultTemplate';
import { useMakeDefaultTemplate } from 'src/data-capture/hooks/templates/useMakeDefaultTemplate';
import useTemplates from 'src/data-capture/hooks/templates/useTemplates';
import { Button } from 'src/design-system/Button';
import { useFlags } from 'src/feature-flags/FlagsHook';
import { ChooseDefaultTemplateModal } from './ChooseDefaultTemplateModal';
import { DefaultTemplateNotice } from './DefaultTemplateNotice';
import { NoDefaultPrompt } from './NoDefaultPrompt';

/**
 * Says which template we write automatic notes with, and lets the people who can choose the firm's
 * default change it. At a firm whose default was set for them, they are asked once to keep it or
 * choose another; firms with several templates and no default are asked to choose one.
 */
export function AutomaticNotesCard(boxProps: BoxProps) {
  const { flagEnabledDefaultTemplates } = useFlags();
  if (!flagEnabledDefaultTemplates) return null;
  return <AutomaticNotesCardContent {...boxProps} />;
}

function AutomaticNotesCardContent(boxProps: BoxProps) {
  const canChooseDefault = useCanChooseDefaultTemplate();
  const { files: templates } = useTemplates({ templateType: 'MEETING_NOTE' });
  const { makeDefault, isMakingDefault } = useMakeDefaultTemplate();
  const [isChoosing, setIsChoosing] = useState(false);

  if (needsDefault(templates)) {
    return <NoDefaultPrompt templates={templates} {...boxProps} />;
  }
  const automaticTemplate = automaticNoteTemplate(templates);
  if (!automaticTemplate) return null;

  // With a single active template there is nothing else to choose, and automatic notes use it
  // whether or not anyone confirms it.
  const canChoose = canChooseDefault && templates.filter(template => template.isActive).length > 1;
  const currentDefault = firmDefaultTemplate(templates);
  const unconfirmedDefault =
    canChoose && currentDefault && !currentDefault.defaultChosen ? currentDefault : undefined;

  return (
    <>
      {unconfirmedDefault ? (
        <DefaultTemplateNotice
          title={`We chose ${unconfirmedDefault.displayName} as your default`}
          actions={
            <>
              <Button
                variant="primary"
                size="xs"
                onClick={() => makeDefault(unconfirmedDefault)}
                loading={isMakingDefault}
              >
                Keep it
              </Button>
              <Button
                variant="secondary"
                size="xs"
                onClick={() => setIsChoosing(true)}
                disabled={isMakingDefault}
              >
                Choose another
              </Button>
            </>
          }
          {...boxProps}
        >
          {
            "It's the template we use to write notes automatically after every recorded meeting, for everyone at your firm. Keep it, or choose the one that suits your firm."
          }
        </DefaultTemplateNotice>
      ) : (
        <DefaultTemplateNotice
          title="Automatic notes are on"
          actions={
            canChoose ? (
              <Button variant="text" onClick={() => setIsChoosing(true)}>
                Change default
              </Button>
            ) : undefined
          }
          {...boxProps}
        >
          {`After every recorded meeting, we will write up your meeting notes using your ${automaticTemplate.displayName} template.`}
          {canChooseDefault ? '' : ' A system admin at your firm chooses the default.'}
        </DefaultTemplateNotice>
      )}
      <ChooseDefaultTemplateModal
        opened={isChoosing}
        onClose={() => setIsChoosing(false)}
        templates={templates}
      />
    </>
  );
}
